import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { SmallSpinner } from "@/react/components/Spinner";

/* ========== Attachment Context ========== */
const AttachmentContext = createContext(null);
export const useAttachment = () => useContext(AttachmentContext);

/* ========== AttachmentThumbnails ========== */
export default function AttachmentThumbnails({
	attachments = [],
	removeAttachment = null,
	retryAttachment = null,
	...props
}) {
	const [viewerIndex, setViewerIndex] = useState(null);

	if (attachments.length === 0) return null;

	return (
		<AttachmentContext.Provider value={{ attachments, removeAttachment, retryAttachment }}>
			<div className="flex gap-4 w-full flex-wrap items-center" {...props}>
				{attachments.map((fileProps, index) =>
					fileProps.type === "image" ? (
						<ImageThumbnail key={fileProps.uuid || index} fileProps={fileProps} onOpen={() => setViewerIndex(index)} />
					) : (
						<FileThumbnail key={fileProps.uuid || index} fileProps={fileProps} onOpen={() => setViewerIndex(index)} />
					)
				)}
			</div>

			{viewerIndex !== null && (
				<AttachmentViewer attachments={attachments} index={viewerIndex} setIndex={setViewerIndex} />
			)}
		</AttachmentContext.Provider>
	);
} 

/* ========== Common Close Button ========== */
function ThumbnailCloseButton({ onClick }) {
	return (
		<button
			className="btn p-0.5 btn-secondary btn-round absolute -top-2 -right-2 transition-all z-10"
			onClick={onClick}
		>
			<span className="font-MaterialIcons text-lg">close</span>
		</button>
	);
}

/* ========== Image Thumbnail ========== */
function ImageThumbnail({ fileProps, onOpen }) {
	const { removeAttachment, retryAttachment } = useAttachment();
	const { name, url, isFailed, isPending, uuid } = fileProps;

	return (
		<div className="relative">
			<div className={`flex items-center w-20 h-20 justify-center border-normal rounded-primary overflow-clip group ${isFailed ? "border-secondary" : "border-primary"} transition-all`}>
				{isPending && <SmallSpinner className="!w-8 !h-8" />}
				{isFailed && (
					<div className="flex flex-col items-center justify-center h-full w-full text-center p-2">
						<div className="text-secondary group-hover:opacity-0 transition-all">
							<span className="font-MaterialIcons text-2xl">error</span>
							<p className="text-xs mb-2">Upload failed</p>
						</div>
						{retryAttachment && (
							<div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
								<div
									className="flex text-secondary animation-pop-out text-xs cursor-pointer font-normal flex-col"
									onClick={() => retryAttachment(uuid)}
								>
									<span className="font-MaterialIcons text-3xl font-normal align-middle">refresh</span>
									Retry
								</div>
							</div>
						)}
						{removeAttachment && <ThumbnailCloseButton onClick={() => removeAttachment(uuid)} />}
					</div>
				)}
				{!isPending && !isFailed && (
					<div className="w-full h-full">
						<img src={url} alt={name} className="h-full w-full object-cover" />
						{removeAttachment && <ThumbnailCloseButton onClick={() => removeAttachment(uuid)} />}
						{onOpen && (
							<div
								className="absolute cursor-pointer inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity text-primary bg-white/40 dark:bg-white/40 rounded-primary"
								tabIndex={-1}
								onClick={onOpen}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

/* ========== File Thumbnail ========== */
function FileThumbnail({ fileProps, onOpen }) {
	const { removeAttachment, retryAttachment } = useAttachment();
	const { name, isPending, isFailed, uuid } = fileProps;

	return (
		<div className={`relative flex items-center gap-2 border-normal rounded-md p-2 w-[180px] h-18 ${isFailed ? "border-secondary" : "border-primary"}`} title={name}>
			<div className={`flex items-center justify-center rounded-primary bg-primary-100 h-10 w-10 shrink-0 overflow-clip ${isFailed && "!bg-secondary/10"}`}>
				{isPending && <SmallSpinner className="!w-5 !h-5" />}
				{isFailed && retryAttachment && (
					<button
						className="animation-pop-out bg-transparent flex flex-col items-center justify-center text-secondary"
						onClick={() => retryAttachment(uuid)}
					>
						<span className="font-MaterialIcons text-2xl font-normal">refresh</span>
					</button>
				)}
				{!isPending && !isFailed && <span className="font-MaterialIconsNew text-4xl font-normal text-primary">docs</span>}
				{onOpen && !isPending && !isFailed && (
					<div
						className="absolute cursor-pointer inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity text-primary bg-white/40 dark:bg-white/40 rounded-primary"
						tabIndex={-1}
						onClick={onOpen}
					/>
				)}
			</div>
			<div className="flex flex-col gap-1 justify-center overflow-hidden">
				<span className="truncate text-sm">{name}</span>
				{isFailed && <span className="text-xs text-secondary">Upload failed</span>}
			</div>
			{removeAttachment && <ThumbnailCloseButton onClick={() => removeAttachment(uuid)} />}
		</div>
	);
}

/* ========== Attachment Viewer ========== */
function AttachmentViewer({ index, setIndex }) {
	const { attachments } = useAttachment();
	const file = attachments[index];

	const close = useCallback(() => setIndex(null), [setIndex]);
	const prev = useCallback(() => setIndex(i => (i > 0 ? i - 1 : i)), [setIndex]);
	const next = useCallback(() => setIndex(i => (i < attachments.length - 1 ? i + 1 : i)), [attachments.length, setIndex]);

	useEffect(() => {
		const handleKey = e => {
			if (e.key === "Escape") close();
			if (e.key === "ArrowLeft" && index > 0) prev();
			if (e.key === "ArrowRight" && index < attachments.length - 1) next();
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [close, prev, next, index, attachments.length]);

	if (!file) return null;

	return createPortal(
		<div className="fixed inset-0 bg-black/90 dark:bg-white/90 z-50 flex items-center justify-center">
			<button className="btn p-0.5 btn-round absolute top-4 right-4 bg-transparent transition-all z-10" onClick={close}>
				<span className="font-MaterialIcons text-3xl text-primary">close</span>
			</button>

			{index > 0 && (
				<button className="btn btn-round absolute left-12 text-white text-4xl border-primary border bg-transparent" onClick={prev}>
					<span className="font-MaterialIcons text-4xl text-primary">arrow_back_ios_new</span>
				</button>
			)}

			{file.type === "image" ? (
				<img src={file.url} alt={file.name} className="max-h-[90%] max-w-[90%] object-contain rounded" />
			) : (
				<div className="p-4 bg-white w-[480px] border-normal rounded flex items-center">
					<span className="font-MaterialIconsNew text-9xl text-black font-normal -mr-2">docs</span>
					<div className="flex flex-col gap-2 w-full text-ellipsis overflow-clip items-start pl-2">
						<p className="text-black font-bold text-ellipsis overflow-clip">{file.name}</p>
						<a href={file.url} download={file.name} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
							Download
						</a>
					</div>
				</div>
			)}

			{index < attachments.length - 1 && (
				<button className="btn btn-round absolute right-12 text-white text-4xl border-primary border bg-transparent" onClick={next}>
					<span className="font-MaterialIcons text-4xl text-primary">arrow_forward_ios</span>
				</button>
			)}
		</div>,
		document.body
	);
}
